import { CheckIcon, ChevronRightIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/Badge';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { LoadMoreButton } from '@/components/RunsTable/LoadMoreButton';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useCommitsInfinite } from '@/hooks/useSwr';
import ExternalLinkIcon from '@/icons/ExternalLinkIcon.svg?react';
import { CommitWithLookupsSchema, RepoWithLookupsSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import {
  getPromptDisplayName,
  getPromptDisplayNameFromRepoObj,
  getPromptIcon,
} from '../utils/Prompts.utils';
import { TabContentProps } from './types';

const PromptCommits = ({
  owner,
  repo,
  lastCommitHash,
  selectedCommitHash,
  handleSelect,
  isCurrentPrompt,
}: {
  owner: string;
  repo: string;
  lastCommitHash: string | null;
  selectedCommitHash?: string;
  handleSelect: (commit: CommitWithLookupsSchema) => void;
  isCurrentPrompt: boolean;
}) => {
  const {
    data: commitsPages,
    isLoading: commitsLoading,
    size,
    setSize,
    isValidating,
  } = useCommitsInfinite(owner, repo);
  const allCommits = useMemo(
    () => commitsPages?.flatMap((page) => page.commits) ?? [],
    [commitsPages]
  );
  const hasMore = (commitsPages?.[0]?.total ?? 0) > allCommits.length;
  const isNextPageLoading = isValidating && size > 1 && !!commitsPages?.length;

  const renderCommits = () => {
    if (commitsLoading) {
      return <LinearProgress />;
    }
    return allCommits.map((commit) => {
      const commitHashDisplay = commit.commit_hash.substring(0, 8);
      const isLatest = commit.commit_hash === lastCommitHash;
      const isSelected =
        isCurrentPrompt &&
        (commit.commit_hash === selectedCommitHash ||
          commitHashDisplay === selectedCommitHash ||
          (isLatest && selectedCommitHash === 'latest'));
      return (
        <button
          key={commit.commit_hash}
          onClick={() => handleSelect(commit)}
          type="button"
          className={cn(
            'flex h-9 w-full cursor-pointer items-center gap-4 rounded-md p-2 text-left hover:bg-secondary'
          )}
        >
          <div className="flex w-full flex-row items-center justify-between gap-2 px-1">
            <div className="flex flex-row items-center gap-2">
              <span className="line-clamp-1 text-xs">{commitHashDisplay}</span>
              {isLatest && <Badge text="latest" />}
            </div>
            {isSelected && (
              <CheckIcon className="h-5 w-5 flex-shrink-0 text-brand-tertiary" />
            )}
          </div>
        </button>
      );
    });
  };

  return (
    <div className="max-h-[300px] overflow-y-auto">
      <div className="sticky top-0 flex items-center gap-1 bg-background p-2 pb-1 text-sm text-gray-500">
        <span className="mx-auto truncate text-xs font-medium text-tertiary">
          SELECT COMMIT
        </span>
      </div>
      {renderCommits()}
      {hasMore && (
        <LoadMoreButton
          isInitialLoad
          isLoading={isNextPageLoading}
          onClick={() => setSize((size) => size + 1)}
        />
      )}
    </div>
  );
};

const PromptItem = ({
  prompt,
  handlePromptSelect,
  showCommitSelector,
  isCurrent,
  selectedCommitHash,
}: {
  prompt: RepoWithLookupsSchema;
  handlePromptSelect: (
    prompt: RepoWithLookupsSchema,
    commit?: CommitWithLookupsSchema
  ) => void;
  showCommitSelector?: boolean;
  isCurrent: boolean;
  selectedCommitHash?: string;
}) => {
  const displayName = getPromptDisplayName(
    prompt.is_public,
    prompt.full_name,
    prompt.repo_handle,
    prompt.last_commit_hash ?? undefined
  );
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (commit?: CommitWithLookupsSchema) => {
    handlePromptSelect(prompt, commit);
  };

  const renderItem = () => {
    return (
      <CommandItem
        key={prompt.id}
        onSelect={() => handleSelect()}
        className="flex items-center gap-2"
        value={displayName}
      >
        <div className="flex w-full flex-row items-center gap-2">
          {getPromptIcon(prompt.is_public)}
          <TextOverflowTooltip>
            {getPromptDisplayName(
              prompt.is_public,
              prompt.full_name,
              prompt.repo_handle
            )}
          </TextOverflowTooltip>
          {isCurrent && <Badge text="current" />}
          {showCommitSelector && (
            <ChevronRightIcon className="ml-auto h-4 w-4 flex-shrink-0" />
          )}
        </div>
      </CommandItem>
    );
  };

  if (!showCommitSelector) {
    return renderItem();
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild onMouseEnter={() => setIsOpen(true)}>
        {renderItem()}
      </PopoverTrigger>
      <PopoverContent
        side="right"
        sideOffset={5}
        className="w-[215px] p-0"
        align="start"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <PromptCommits
          owner={prompt.owner}
          repo={prompt.repo_handle}
          lastCommitHash={prompt.last_commit_hash}
          handleSelect={handleSelect}
          selectedCommitHash={selectedCommitHash}
          isCurrentPrompt={isCurrent}
        />
      </PopoverContent>
    </Popover>
  );
};

export const PromptSelectorTabContent: React.FC<TabContentProps> = ({
  selectedPromptIdentifier,
  selectedCommitHash,
  repos,
  isLoading,
  error,
  hasMore,
  isNextPageLoading,
  setSize,
  showNewPromptLink,
  emptyStateText = 'No prompts found.',
  newPromptText = 'Create a new prompt',
  newLink,
  handlePromptSelect,
  showCommitSelector,
  searchWithoutTags,
  setSearchWithoutTags,
}) => {
  const { selectedTags } = useStoredResourceTags();
  if (error) {
    return (
      <span className="flex items-center px-2 py-1 text-error">
        Error loading prompts.
      </span>
    );
  }

  if (isLoading && repos.length === 0) {
    return <LinearProgress />;
  }

  if (repos.length === 0) {
    return (
      <div className="flex flex-col gap-1 py-3 text-center">
        {searchWithoutTags || selectedTags.length === 0 ? (
          <span>{emptyStateText}</span>
        ) : (
          <span className="flex flex-col gap-2">
            No prompts found with the selected resource tags.
            <button
              type="button"
              onClick={() => setSearchWithoutTags(true)}
              className="self-center rounded-md border border-primary px-2 py-1 text-sm text-primary"
            >
              Search all prompts
            </button>
          </span>
        )}
        {showNewPromptLink && newLink && (
          <Link
            to={newLink}
            className="mx-auto inline-flex items-center gap-1 text-sm text-tertiary outline-none hover:underline"
          >
            <span>{newPromptText}</span>
            <ExternalLinkIcon className="h-4 w-4" />
          </Link>
        )}
      </div>
    );
  }

  return (
    <Command>
      <CommandList className="max-h-[30vh] overflow-y-auto">
        <CommandGroup heading="" className="m-0">
          {repos.map((repo) => (
            <PromptItem
              key={repo.id}
              prompt={repo}
              handlePromptSelect={handlePromptSelect}
              showCommitSelector={showCommitSelector}
              isCurrent={Boolean(
                repo.id === selectedPromptIdentifier ||
                  selectedPromptIdentifier?.startsWith(
                    getPromptDisplayNameFromRepoObj(repo)
                  )
              )}
              selectedCommitHash={selectedCommitHash}
            />
          ))}
          {hasMore && (
            <div className="flex grow flex-col items-center">
              <LoadMoreButton
                isInitialLoad
                isLoading={isNextPageLoading}
                onClick={() => setSize((size) => size + 1)}
              />
            </div>
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};
